/**
* @Author: Michael Neumair <mBook>
* @Date:   2016-08-22T13:39:18+02:00
* @Email:  7q7w7e7r@gmail.com
* @Last modified by:   mBook
* @Last modified time: 2016-09-29T14:10:16+02:00
*/


import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {isEqualDate} from './dateUtils';
import ODatePickerDialog from './ODatePickerDialog';
import AButtonIconInput from 'common/inputs/components/AButtonIconInput';
import {DIALOG} from './../constants/container';
import modeConstants from './../constants/mode';

/**
 * ODatePickerButton - …
 */
class ODatePickerButton extends Component {
    date = undefined;

    state = {};

    /**
     * react lifecycle componentWillUnmount() <ODatePickerButton>
     *
     * @return {undefined}
     */
    componentWillMount() {
      this.date = this.isControlled() ? this.getControlledDate() : this.props.defaultDate;
    }

    /**
     * react lifecycle componentWillReceiveProps() <ODatePickerButton>
     *
     * @param {object} nextProps
     * @return {undefined}
     */
    componentWillReceiveProps(nextProps) {
      if (this.isControlled()) {
        const newDate = this.getControlledDate(nextProps);
        if (!isEqualDate(this.date, newDate)) {
          this.date = newDate;
        }
      }
    }

    /**
     * react render() <ODatePickerButton>
     *
     * @return {object}
     */
    render() {
      const {
        autoOk,
        labelCancel,
        container,
        defaultDate, // eslint-disable-line no-unused-vars
        disableYearSelection,
        firstDayOfWeek,
        disabled,
        maxDate,
        minDate,
        mode,
        labelOk,
        onDismiss,
        onShow,
        onPress, // eslint-disable-line no-unused-vars
        shouldDisableDate,
        style,
        ...other
      } = this.props;


      return (
        <div className="o-date-picker o-date-picker-button" style={style}>
          <AButtonIconInput
            icon="calendar"
            onPress={(ev) => this.handleOnPress(ev)}
            disabled={disabled}
            tabIndex="-1"
          />
          <ODatePickerDialog
            ref={(ref) => this.refDialogWindow(ref)}
            autoOk={autoOk}
            labelCancel={labelCancel}
            container={container}
            disableYearSelection={disableYearSelection}
            firstDayOfWeek={firstDayOfWeek}
            initialDate={this.state.dialogDate}
            maxDate={maxDate}
            minDate={minDate}
            mode={mode}
            labelOk={labelOk}
            onAccept={(selectedVal) => this.handleAccept(selectedVal)}
            onShow={onShow}
            onDismiss={onDismiss}
            shouldDisableDate={shouldDisableDate}
            startWithYears={this.props.startWithYears}
          />
        </div>
      );
    }

    /**
     * hold a ref for operations that must take place direcly on dom
     *
     * @param {DOMNode} comp
     * @return {undefined}
     */
    refDialogWindow(comp) {
      this._dialogWindow = comp;
    }

    /**
     * callback for date selected through an <ODatePickerDialog>
     *
     * @param {date} date
     * @return {undefined}
     */
    handleAccept(date) {
      if (!this.isControlled()) {
        this.date = date;
      }
      if (this.props.onChange) {
        this.props.onChange(null, date);
      }
    };

    /**
     * callback for datepicker requested via button/input
     *
     * @param {object} event
     * @return {undefined}
     */
    handleOnPress(event) {
      if (this.props.onPress) {
        this.props.onPress(event);
      }

      if (!this.props.disabled) {
        setTimeout(() => {
          this.openDialog();
        }, 0);
      }
    };

    /**
     * get date
     *
     * @return {date}
     */
    getDate() {
      return this.date;
    }

    /**
     * Open the date-picker dialog programmatically from a parent.
     *
     * @return {undefined}
     */
    openDialog() {
      /**
        * if the date is not selected then set it to new date
        * (get the current system date while doing so)
        * else set it to the currently selected date
        */
      if (this.date instanceof Date && !isNaN(this.date.getTime())) {
        this.setState({
          dialogDate: this.getDate(),
        }, this._dialogWindow.show);
      } else {
        this.setState({
          dialogDate: new Date(),
        }, this._dialogWindow.show);
      }
    }

    /**
     * focus handler ( alias on openDialog() )
     *
     * @return {undefined}
     */
    focus() {
      this.openDialog();
    }

    /**
     * check if input passed an controlled value through react
     *
     * @return {undefined}
     */
    isControlled() {
      return this.props.hasOwnProperty('value');
    }

    /**
     * gets value if isControlled
     *
     * @param {object} props
     * @return {(date|null)}
     */
    getControlledDate(props = this.props) {
      if (props.value instanceof Date) {
        return props.value;
      }
      return null;
    }
}
ODatePickerButton.propTypes = {
  /**
    * If true, automatically accept and close the picker on select a date.
    */
  autoOk: PropTypes.bool,
  /**
    * Override the default text of the 'Cancel' button.
    */
  labelCancel: PropTypes.node,
  /**
    * Override the default text of the 'OK' button.
    */
  labelOk: PropTypes.node,
  /**
    * Used to control how the Date Picker will be displayed when the input field is focused.
    * `dialog` (default) displays the DatePicker as a dialog with a modal.
    * `inline` displays the DatePicker below the input field (similar to auto complete).
    */
  container: PropTypes.oneOf([DIALOG]),
  /**
    * This is the initial date value of the component.
    * If either `value` or `valueLink` is provided they will override this
    * prop with `value` taking precedence.
    */
  defaultDate: PropTypes.object,
  /**
    * Disables the year selection in the date picker.
    */
  disableYearSelection: PropTypes.bool,
  /**
    * Disables the DatePicker.
    */
  disabled: PropTypes.bool,
  /**
    * Used to change the first day of week. It varies from
    * Saturday to Monday between different locales.
    * The allowed range is 0 (Sunday) to 6 (Saturday).
    * The default is `1`, Monday, as per ISO 8601.
    */
  firstDayOfWeek: PropTypes.number,
  /**
    * The ending of a range of valid dates. The range includes the endDate.
    * The default value is current date + 100 years.
    */
  maxDate: PropTypes.object,
  /**
    * The beginning of a range of valid dates. The range includes the startDate.
    * The default value is current date - 100 years.
    */
  minDate: PropTypes.object,
  /**
    * Tells the component to display the picker in portrait or landscape mode.
    */
  mode: PropTypes.oneOf(Object.values(modeConstants)),
  /**
    * Callback function that is fired when the date value changes.
    *
    * @param {null} null Since there is no particular event associated with the change,
    * the first argument will always be null.
    * @param {object} date The new date.
    */
  onChange: PropTypes.func,
  /**
    * Callback function that is fired when the Date Picker's dialog is dismissed.
    */
  onDismiss: PropTypes.func,
  /**
    * Callback function that is fired when the Date Picker's `TextField` gains focus.
    */
  onShow: PropTypes.func,
  /**
    * Callback function that is fired when a press event occurs on the Date Picker's `Input`.
    *
    * @param {object} event Click event targeting the `Input`.
    */
  onPress: PropTypes.func,
  /**
    * Callback function used to determine if a day's entry should be disabled on the calendar.
    *
    * @param {object} day Date object of a day.
    * @returns {boolean} Indicates whether the day should be disabled.
    */
  shouldDisableDate: PropTypes.func,
  /**
    * Override the inline-styles of the root element.
    */
  style: PropTypes.object,
  /**
    * Sets the date for the Date Picker programmatically.
    */
  value: PropTypes.object,
};
ODatePickerButton.defaultProps = {
  autoOk: false,
  container: DIALOG,
  disabled: false,
  disableYearSelection: false,
  firstDayOfWeek: 1,
  style: {},
};
export default ODatePickerButton;

